<script lang="ts">
	import { Button, Col, Row, Slider, Stack, Toggle } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	const dispatch = createEventDispatcher();

	export let settings: any = {};

	let brightness: number = settings.presence?.leds?.brightness ?? 55;
	let nightModeEnabled: boolean = settings.presence?.leds?.night_mode?.enable ?? false;
	let nightModeBrightness: number = settings.presence?.leds?.night_mode?.brightness ?? 20;
	let nightModeStart: string = settings.presence?.leds?.night_mode?.active_between?.[0] ?? '';
	let nightModeEnd: string = settings.presence?.leds?.night_mode?.active_between?.[1] ?? '';

	function handleDiscard() {
		brightness = settings.presence?.leds?.brightness ?? 55;
		nightModeEnabled = settings.presence?.leds?.night_mode?.enable ?? false;
		nightModeBrightness = settings.presence?.leds?.night_mode?.brightness ?? 20;
		nightModeStart = settings.presence?.leds?.night_mode?.active_between?.[0] ?? '';
		nightModeEnd = settings.presence?.leds?.night_mode?.active_between?.[1] ?? '';
	}

	function handleSave() {
		const activeBetween: string[] =
			nightModeEnabled && nightModeStart && nightModeEnd ? [nightModeStart, nightModeEnd] : [];

		dispatch('configSave', {
			leds: {
				brightness: Number(brightness),
				night_mode: {
					enable: nightModeEnabled,
					brightness: Number(nightModeBrightness),
					active_between: activeBetween,
				},
			},
		});
	}

	const brightnessA11y = {
		sliderValueText: `Current brightness value is ${brightness}`,
		ariaLabel: 'LED brightness',
		ariaLabelValue: `Current brightness value is ${brightness}`,
	};

	const nightBrightnessA11y = {
		sliderValueText: `Current night brightness value is ${nightModeBrightness}`,
		ariaLabel: 'Night mode brightness',
		ariaLabelValue: `Current night brightness value is ${nightModeBrightness}`,
	};
</script>

<Stack gap="s" class="mt-s">
	<!-- LED brightness -->
	<Row>
		<Col>
			<p role="presentation" class="section-title">
				{$format('presence_led_indicator_title', { default: 'LED indicator' })}
			</p>
			<p class="brightness-label">
				{$format('presence_led_brightness_label', {
					values: { value: brightness },
					default: `Brightness ${brightness}%`,
				})}
			</p>
			<Slider
				id="slider-led-brightness"
				a11yProps={brightnessA11y}
				min={0}
				withOverlay={false}
				stopValueLabel
				value={brightness}
				type="horizontal"
				on:value_change={(e) => (brightness = e.detail.value)}
			/>
		</Col>
	</Row>

	<hr class="mt-m mb-s" />

	<!-- Night mode -->
	<Row>
		<Col>
			<p role="presentation" class="section-title">
				{$format('presence_led_night_mode_title', { default: 'Night mode' })}
			</p>
		</Col>
	</Row>
	<Row>
		<Col>
			<p role="presentation" class="mb-s body-styles">
				{$format('presence_led_enable_night_mode', { default: 'Enable night mode' })}
			</p>
			<Toggle
				aria-label={$format('toggle_night_mode', { default: 'Enable night mode' })}
				bind:checked={nightModeEnabled}
				size="m"
				id="toggle-night-mode"
			/>
		</Col>
	</Row>

	{#if nightModeEnabled}
		<Row>
			<Col sm="6">
				<label class="time-label" for="input-night-start"
					>{$format('presence_led_start_time', { default: 'Start time' })}</label
				>
				<input
					class="time-input"
					type="time"
					id="input-night-start"
					bind:value={nightModeStart}
					aria-label="Night mode start time"
				/>
			</Col>
			<Col sm="6">
				<label class="time-label" for="input-night-end"
					>{$format('presence_led_end_time', { default: 'End time' })}</label
				>
				<input
					class="time-input"
					type="time"
					id="input-night-end"
					bind:value={nightModeEnd}
					aria-label="Night mode end time"
				/>
			</Col>
		</Row>
		<Row>
			<Col>
				<p class="brightness-label">
					{$format('presence_led_brightness_label', {
						values: { value: nightModeBrightness },
						default: `Brightness ${nightModeBrightness}%`,
					})}
				</p>
				<Slider
					id="slider-night-brightness"
					a11yProps={nightBrightnessA11y}
					min={0}
					withOverlay={false}
					stopValueLabel
					value={nightModeBrightness}
					type="horizontal"
					on:value_change={(e) => (nightModeBrightness = e.detail.value)}
				/>
			</Col>
		</Row>
	{/if}

	<hr class="mt-l mb-s" />
	<Row>
		<Col>
			<div class="config-actions">
				<Button variant="secondary" size="m" on:click={handleDiscard}>
					{$format('button_discard_changes', { default: 'Discard changes' })}
				</Button>
				<Button variant="primary" size="m" on:click={handleSave}>
					{$format('button_save_settings', { default: 'Save settings' })}
				</Button>
			</div>
		</Col>
	</Row>
</Stack>

<style>
	.section-title {
		font-weight: 600;
		font-size: 14px;
		margin: 0 0 12px 0;
	}

	.brightness-label {
		font-size: 13px;
		color: var(--color-text-secondary);
		margin: 0 0 8px 0;
	}

	.time-label {
		display: block;
		font-size: 13px;
		color: var(--color-text-secondary);
		margin-bottom: 6px;
	}

	.time-input {
		width: 100%;
		padding: 8px 10px;
		background: var(--color-surface-secondary, rgba(255, 255, 255, 0.06));
		border: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		border-radius: 8px;
		color: var(--color-text-primary);
		font-size: 14px;
		box-sizing: border-box;
	}

	.time-input:focus {
		outline: none;
		border-color: var(--color-primary, #0088ff);
	}

	.config-actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
	}
</style>
